import { FC, useMemo } from 'react';
import { format, addDays, startOfDay } from 'date-fns';
import { Calendar } from 'lucide-react';
import { Card } from '../ui/Card';
import { useCalendarNavigation } from './useCalendarNavigation';
import { CalendarHeader } from './CalendarHeader';
import { CalendarGrid } from './CalendarGrid';
import { TimeSlotPicker } from './TimeSlotPicker';
import { AvailabilityLegend } from './AvailabilityLegend';
import { useAvailability, useAvailableDates } from '../../hooks/useAvailability';
import { useTherapistsForService } from '../../hooks/useTherapists';
import { AvailabilityCalendarProps, CalendarTimeSlot } from './types';

const DEFAULT_DAYS_AHEAD = 60;

export const AvailabilityCalendar: FC<AvailabilityCalendarProps> = ({
  serviceId,
  therapistId,
  selectedDate,
  selectedTime,
  onDateSelect,
  onTimeSelect,
  minDate: propMinDate,
  maxDate: propMaxDate,
}) => {
  // Calculate date bounds
  const today = startOfDay(new Date());
  const minDate = propMinDate || today;
  const maxDate = propMaxDate || addDays(today, DEFAULT_DAYS_AHEAD);

  // Calendar navigation
  const { currentDate, goToPrevious, goToNext, goToToday, canGoPrevious, canGoNext } =
    useCalendarNavigation({
      initialDate: selectedDate || today,
      minDate,
      maxDate,
    });

  // When no therapist preference, fetch available therapists for the service
  // and use the first one for availability queries
  const { data: therapistsForService } = useTherapistsForService(serviceId);

  // Determine therapist ID for availability queries
  // Priority: explicit selection > first available therapist
  const therapistIdForQuery = useMemo(() => {
    if (therapistId) return therapistId;
    // Use first available therapist if no preference selected
    const firstTherapist = therapistsForService?.[0];
    if (firstTherapist) {
      return firstTherapist.id;
    }
    return null;
  }, [therapistId, therapistsForService]);

  // Fetch available dates for the current month view
  // Only fetch if we have a therapist ID
  const { data: availableDatesInfo = [], isLoading: isLoadingDates } = useAvailableDates({
    therapistId: therapistIdForQuery || '',
    serviceId,
    startDate: format(minDate, 'yyyy-MM-dd'),
    endDate: format(maxDate, 'yyyy-MM-dd'),
  });

  // Fetch time slots for the selected date
  const { data: availability, isLoading: isLoadingSlots } = useAvailability({
    therapistId: therapistIdForQuery || '',
    serviceId,
    date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
  });

  // Convert TimeSlot[] to CalendarTimeSlot[]
  const timeSlots: CalendarTimeSlot[] = useMemo(() => {
    if (!availability?.slots) return [];
    return availability.slots.map((slot) => ({
      startTime: slot.startTime,
      endTime: slot.endTime,
      isAvailable: slot.isAvailable,
    }));
  }, [availability]);

  // Handle date selection from calendar grid
  const handleDateSelect = (date: Date) => {
    onDateSelect(date);
  };

  // Handle time slot selection
  const handleTimeSelect = (slot: CalendarTimeSlot) => {
    if (!slot.isAvailable) return;
    onTimeSelect(slot);
  };

  return (
    <div className="space-y-6">
      {/* Calendar Card */}
      <Card variant="outlined" padding="md">
        <CalendarHeader
          currentDate={currentDate}
          onPrevious={goToPrevious}
          onNext={goToNext}
          onToday={goToToday}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
        />

        <CalendarGrid
          currentDate={currentDate}
          selectedDate={selectedDate}
          availableDatesInfo={availableDatesInfo}
          onDateSelect={handleDateSelect}
          minDate={minDate}
          maxDate={maxDate}
          isLoading={isLoadingDates}
        />

        <AvailabilityLegend />
      </Card>

      {/* Time Slot Picker - shown when a date is selected */}
      {selectedDate && (
        <Card variant="outlined" padding="md">
          <TimeSlotPicker
            slots={timeSlots}
            selectedTime={selectedTime}
            onTimeSelect={handleTimeSelect}
            isLoading={isLoadingSlots}
            selectedDate={selectedDate}
          />
        </Card>
      )}

      {/* Empty state when no date selected */}
      {!selectedDate && (
        <div className="rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-6 text-center">
          <Calendar className="mx-auto h-12 w-12 text-gray-400 dark:text-slate-500" aria-hidden="true" />
          <p className="mt-2 text-gray-600 dark:text-slate-400">Select a date above to see available time slots.</p>
        </div>
      )}
    </div>
  );
};

AvailabilityCalendar.displayName = 'AvailabilityCalendar';
